import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
} from '@angular/core';
import { KzPopup } from '../popup/kz-popup';
import { KzPopupService } from '../popup/kz-popup.service';
import { placeElement, Position } from '../../utils/positioning';
import { KzTooltipComponent } from './kz-tooltip/kz-tooltip.component';
import { TooltipArrowPosition, TooltipSize, TooltipContext } from './tooltip-options';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { delay, filter, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { Subscription } from 'rxjs/internal/Subscription';

const arrowPositionFromPosition = {
  top: 'bottom-center',
  right: 'left-center',
  bottom: 'top-center',
  left: 'right-center',
  'top-left': 'bottom-left',
  'top-center': 'bottom-center',
  'top-right': 'bottom-right',
  'bottom-left': 'top-left',
  'bottom-center': 'top-center',
  'bottom-right': 'top-right',
  'left-top': 'right-top',
  'left-center': 'right-center',
  'left-bottom': 'right-bottom',
  'right-top': 'left-top',
  'right-center': 'left-center',
  'right-bottom': 'left-bottom',
};

@Directive({
  selector: '[kzTooltip]',
  exportAs: 'kzTooltip',
})
export class KzTooltipDirective implements OnDestroy {
  private _popup: KzPopup<KzTooltipComponent>;

  private _shouldCloseSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _placeElementSubject: Subject<void> = new Subject();
  private _closeSubscription: Subscription;
  private _placeElementSubscription: Subscription;

  private _kzTooltip: string | TemplateRef<any>;

  @Input()
  set kzTooltip(content: string | TemplateRef<any>) {
    this._kzTooltip = content;
  }

  get kzTooltip(): string | TemplateRef<any> {
    return this._kzTooltip;
  }

  private _tooltipPosition: Position = 'top-center';

  get tooltipPosition(): Position {
    return this._tooltipPosition;
  }

  @Input()
  set tooltipPosition(position: Position) {
    this._tooltipPosition = position;
    this.setTooltipArrowPosition();
  }

  private _tooltipContext: TooltipContext = 'default';

  get tooltipContext(): TooltipContext {
    return this._tooltipContext;
  }

  @Input()
  set tooltipContext(type: TooltipContext) {
    this._tooltipContext = type;
  }

  private _tooltipSize: TooltipSize = 'default';

  get tooltipSize(): TooltipSize {
    return this._tooltipSize;
  }

  @Input()
  set tooltipSize(size: TooltipSize) {
    this._tooltipSize = size;
  }


  private _tooltipHideArrow = false;

  @Input()
  set tooltipHideArrow(hide: boolean) {
    this._tooltipHideArrow = hide;
  }

  private _tooltipArrowPosition: TooltipArrowPosition = 'bottom-center';

  get tooltipArrowPosition(): TooltipArrowPosition {
    return this._tooltipArrowPosition;
  }

  private _tooltipAutoOpen = true;

  get tooltipAutoOpen(): boolean {
    return this._tooltipAutoOpen;
  }

  @Input()
  set tooltipAutoOpen(autoOpen: boolean) {
    this._tooltipAutoOpen = autoOpen;
  }

  private _tooltipAutoClose = true;

  get tooltipAutoClose(): boolean {
    return this._tooltipAutoClose;
  }

  @Input()
  set tooltipAutoClose(autoClose: boolean) {
    this._tooltipAutoClose = autoClose;
  }

  private _tooltipOffsetLeft = 0;

  get tooltipOffsetLeft(): number {
    return this._tooltipOffsetLeft;
  }

  @Input()
  set tooltipOffsetLeft(offsetLeft: number) {
    this._tooltipOffsetLeft = offsetLeft;
  }

  private _tooltipPersistDelay: number = 100;

  get tooltipPersistDelay(): number {
    return this._tooltipPersistDelay;
  }

  @Input()
  set tooltipPersistDelay(value: number) {
    this._tooltipPersistDelay = value;
  }

  @Output()
  readonly opened: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  readonly closed: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private _elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _kzPopupService: KzPopupService,
  ) {
  }

  initTooltip() {
    this._popup = this._kzPopupService.create(KzTooltipComponent);
    this._placeElementSubscription = this._placeElementSubject.asObservable().pipe(
      delay(10),
      tap(() => {
        const displayedPosition = placeElement(
          this._elementRef.nativeElement,
          this._popup.componentRef.location.nativeElement,
          this.tooltipPosition,
          undefined, false, false, this.tooltipOffsetLeft
        );
        if (displayedPosition !== this.tooltipPosition) {
          this.tooltipPosition = displayedPosition;
          this.setTooltipArrowPosition();
          this._popup.componentRef.instance.tooltipArrowPosition = this.tooltipArrowPosition;
        }

        this._changeDetectorRef.detectChanges();
        this._changeDetectorRef.markForCheck();
      })
    ).subscribe();
  }

  ngOnDestroy(): void {
    if (this._closeSubscription && !this._closeSubscription.closed) {
      this._closeSubscription.unsubscribe();
    }
    if (this._placeElementSubscription && !this._placeElementSubscription.closed) {
      this._placeElementSubscription.unsubscribe();
    }
    this._popup?.close();
  }

  setTooltipArrowPosition() {
    this._tooltipArrowPosition = (this.tooltipPosition && arrowPositionFromPosition.hasOwnProperty(this.tooltipPosition))
      ? arrowPositionFromPosition[this.tooltipPosition as string]
      : null;
  }

  toggle() {
    if (this._popup?.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this._popup) {
      this.initTooltip();
    }
    this._popup.open(this.kzTooltip);
    // Fix for preventing screen blink on tooltip open.
    this._popup.componentRef.location.nativeElement.style.position = 'fixed';
    this._popup.componentRef.instance.clickOutsideExceptions = [this._elementRef];
    this._popup.componentRef.instance.tooltipSize = this.tooltipSize;
    this._popup.componentRef.instance.tooltipContext = this.tooltipContext;
    this._popup.componentRef.instance.tooltipPadding = (typeof this.kzTooltip === 'string');
    this._popup.componentRef.instance.tooltipArrowPosition = this.tooltipArrowPosition;
    this._popup.componentRef.instance.tooltipHideArrow = this._tooltipHideArrow;
    this._popup.componentRef.instance.loadFromDirective = true;
    this.placeTooltipElement();
    if (this._popup?.opened) {
      this.opened.emit();
    }

    if (this._closeSubscription && !this._closeSubscription.closed) {
      this._closeSubscription.unsubscribe();
    }
    if (!!this.tooltipAutoClose) {
      this._initializePersistence();
    } else {
      this._handleOnClose();
    }
  }

  private _handleOnClose() {
    this._closeSubscription = this._popup.componentRef.instance.tooltipClickedOutside.pipe(
      tap(() => this.close())
    ).subscribe();
  }

  private _initializePersistence() {
    this._closeSubscription = merge(
      this._shouldCloseSubject.asObservable().pipe(
        delay(this.tooltipPersistDelay),
        withLatestFrom(this._popup.componentRef.instance.tooltipMouseLeaved.pipe(startWith(true))),
      ),
      this._popup.componentRef.instance.tooltipMouseLeaved.pipe(
        startWith(true),
        delay(this.tooltipPersistDelay),
        withLatestFrom(this._shouldCloseSubject.asObservable()),
      ),
    ).pipe(
      filter(([firstLeaved, secondLeaved]) => firstLeaved && secondLeaved),
      tap(() => this.close())
    ).subscribe();
  }

  close() {
    if (this._closeSubscription && !this._closeSubscription.closed) {
      this._closeSubscription.unsubscribe();
    }
    this._popup?.close();
    this.closed.emit();
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this._shouldCloseSubject.next(false);
    if (!this._popup?.opened && this.tooltipAutoOpen) {
      this.open();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.tooltipAutoClose) {
      this._shouldCloseSubject.next(true);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.placeTooltipElement();
  }

  public placeTooltipElement() {
    if (this._popup?.componentRef) {
      this._placeElementSubject.next();
    }
  }
}
